import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { PagingToken } from '@aya/controls/aya-table';
import { Observable } from 'rxjs';
import { LeadQueue } from 'src/app/lead-queue/models/lead-queue.model';
import { ProfessionGroup, RecruiterLeadInfoStatus } from 'src/app/lead-queue/models/recruiter-lead-Info.model';
import { ReferralsRecruiter } from 'src/app/lead-queue/models/referrals-recruiter.model';
import { Referrals } from 'src/app/lead-queue/models/referrals.model';
import { FetchArgs } from 'src/app/shared/models/core';
import { APP_CONFIG, Settings } from '../config/settings';
import { appendQuery, combine, getFilterQueryString } from '../shared/utilities/route';
import { ProfessionGroupRecruiter } from './models/profession-group-recruiter.model';
import { RecruiterGrouping } from './models/recruiter-grouping.model';
import { RecruiterPauseStatus } from './models/recruiter-pause-status.model';

@Injectable({
    providedIn: 'root'
})
export class LeadQueueService {
    private coreApiPath: string;

    constructor(
        private readonly _httpClient: HttpClient,
        @Inject(APP_CONFIG) settings: Settings
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'AyaNova');
    }

    getReferrals(args: FetchArgs): Observable<PagingToken<Referrals[]>> {
        const headers = this.getHeaders();

        let url = combine(this.coreApiPath, 'leadassignment/referrals-paginated');

        if (args.filter) {
            url += getFilterQueryString(args.filter);
        }

        if (args.sort && args.sort.field && args.sort.field.length) {
            url = appendQuery(url, `sort.Field=${args.sort.field}&sort.Type=${args.sort.dir || 'asc'}`);
        }

        url = appendQuery(url, `pageSize=${args.pageSize}`, `skip=${args.skip}`);

        return this._httpClient.get<PagingToken<Referrals[]>>(url, { headers: headers });
    }

    addToLeadsQueue(userId: number): Observable<LeadQueue> {
        const url = combine(this.coreApiPath, `leadassignment/${userId}`);
        return this._httpClient.post<LeadQueue>(url, {});
    }

    findLeadRecruiters(searchText: string): Observable<ReferralsRecruiter[]> {
        const url = combine(
            this.coreApiPath,
            searchText ? `leadassignment/recruiters/search/${searchText}` : `leadassignment/recruiters/search`
        );
        return this._httpClient.get<ReferralsRecruiter[]>(url, {});
    }

    assignToRecruiter(userId: number, recruiterId: number, isReferral: boolean = false): Observable<unknown> {
        const url = combine(
            this.coreApiPath,
            `leadassignment/${userId}/assignto/${recruiterId}/referral/${isReferral}`
        );
        return this._httpClient.post<unknown>(url, {});
    }

    assignToPerDiemRecruiter(userId: number, recruiterId: number, isReferral: boolean = false): Observable<unknown> {
        const url = combine(
            this.coreApiPath,
            `leadassignment/${userId}/assignto/per-diem/${recruiterId}/referral/${isReferral}`
        );
        return this._httpClient.post<unknown>(url, {});
    }

    assignToPermRecruiter(userId: number, recruiterId: number, isReferral: boolean = false): Observable<unknown> {
        const url = combine(
            this.coreApiPath,
            `leadassignment/${userId}/assignto/perm/${recruiterId}/referral/${isReferral}`
        );
        return this._httpClient.post<unknown>(url, {});
    }

    getRecruiterGrouping(): Observable<RecruiterGrouping> {
        const url = `${this.coreApiPath}/LeadAssignment/Recruiters`;
        return this._httpClient.get<RecruiterGrouping>(url);
    }

    getRecruitersAssignedToProfessionGroup(professionGroupId: number): Observable<ProfessionGroupRecruiter[]> {
        const url = `${this.coreApiPath}/LeadAssignment/profession-group/${professionGroupId}/assigned-recruiters`;
        return this._httpClient.get<ProfessionGroupRecruiter[]>(url);
    }

    getProfessionGroupIdsWithPausedRecruiters(): Observable<number[]> {
        const url = `${this.coreApiPath}/LeadAssignment/profession-groups/paused-recruiters`;
        return this._httpClient.get<number[]>(url);
    }

    updateRecruiterProfessionGroupPauseStatuses(
        professionGroupId: number,
        recruiterPauseStatuses: RecruiterPauseStatus[]
    ): Observable<number> {
        const url = `${this.coreApiPath}/LeadAssignment/recruiter/profession-group/${professionGroupId}/pause-statuses`;
        return this._httpClient.put<number>(url, recruiterPauseStatuses);
    }

    deleteProfessionGroup(groupId: number): Observable<unknown> {
        const url = `${this.coreApiPath}/LeadAssignment/ProfessionGroup/${groupId}`;
        return this._httpClient.delete<unknown>(url);
    }

    createProfessionGroup(name: string, parentGroupId: number): Observable<ProfessionGroup> {
        const params = {
            Name: name,
            ParentGroupId: parentGroupId
        };

        return this._httpClient.put<ProfessionGroup>(`${this.coreApiPath}/LeadAssignment/ProfessionGroup/`, params);
    }

    updateProfessionGroupName(name: string, professionGroupId: number): Observable<ProfessionGroup> {
        const params = {
            Id: professionGroupId,
            Name: name
        };

        return this._httpClient.post<ProfessionGroup>(`${this.coreApiPath}/LeadAssignment/ProfessionGroup/`, params);
    }

    updateProfessionGroupProfession(professionGroupId: number, professionId: number): Observable<unknown> {
        return this._httpClient.put<unknown>(
            `${this.coreApiPath}/LeadAssignment/ProfessionGroup/${professionGroupId}/Profession/${professionId}`,
            null
        );
    }

    deleteProfessionGroupProfession(professionGroupId: number, professionId: number): Observable<unknown> {
        return this._httpClient.delete<unknown>(
            `${this.coreApiPath}/LeadAssignment/ProfessionGroup/${professionGroupId}/Profession/${professionId}`
        );
    }

    updateProfessionGroupProfessionSpecialty(
        professionGroupId: number,
        professionId: number,
        expertiseId: number
    ): Observable<unknown> {
        return this._httpClient.put<unknown>(
            `${this.coreApiPath}/LeadAssignment/ProfessionGroup/${professionGroupId}/Profession/${professionId}/Specialty/${expertiseId}`,
            null
        );
    }

    deleteProfessionGroupProfessionSpecialty(
        professionGroupId: number,
        professionId: number,
        expertiseId: number
    ): Observable<unknown> {
        return this._httpClient.delete<unknown>(
            `${this.coreApiPath}/LeadAssignment/ProfessionGroup/${professionGroupId}/Profession/${professionId}/Specialty/${expertiseId}`
        );
    }

    assignRecruiterToGroup(recruiterId: number, professionGroupId: number): Observable<unknown> {
        return this._httpClient.post<unknown>(
            `${this.coreApiPath}/LeadAssignment/Recruiter/${recruiterId}/ProfessionGroup/${professionGroupId}`,
            null
        );
    }

    unassignRecruiterFromGroup(recruiterId: number, professionGroupId: number): Observable<unknown> {
        return this._httpClient.delete<unknown>(
            `${this.coreApiPath}/LeadAssignment/Recruiter/${recruiterId}/ProfessionGroup/${professionGroupId}`
        );
    }

    assignRecruiterToLocation(recruiterId: number, locationId: number): Observable<unknown> {
        return this._httpClient.put<unknown>(
            `${this.coreApiPath}/LeadAssignment/Recruiter/${recruiterId}/Location/${locationId}`,
            null
        );
    }

    unassignRecruiterFromLocation(recruiterId: number, locationId: number): Observable<unknown> {
        return this._httpClient.delete<unknown>(
            `${this.coreApiPath}/LeadAssignment/Recruiter/${recruiterId}/Location/${locationId}`
        );
    }

    setRecruiterActive(recruiterId: number, status: number): Observable<unknown> {
        return this._httpClient.put<unknown>(
            `${this.coreApiPath}/LeadAssignment/Recruiter/${recruiterId}/Status/${status}`,
            null
        );
    }

    setRecruiterStatus(leadInfoId: number, status: number): Observable<RecruiterLeadInfoStatus> {
        return this._httpClient.put<RecruiterLeadInfoStatus>(
            `${this.coreApiPath}/LeadAssignment/Recruiter/${leadInfoId}/LeadInfoStatus/${status}`,
            null
        );
    }

    getRecruiterLeadQueueInfoStatus(recruiterId: number): Observable<RecruiterLeadInfoStatus> {
        return this._httpClient.get<RecruiterLeadInfoStatus>(
            `${this.coreApiPath}/LeadAssignment/Recruiter/${recruiterId}/get-paused-status`
        );
    }

    exportAssignmentReport(): Observable<HttpResponse<ArrayBuffer>> {
        const url = `${this.coreApiPath}/LeadAssignment/exportAssignmentReport`;
        return this._httpClient.post(url, null, { observe: 'response', responseType: 'arraybuffer' });
    }

    setRecruiterLeadQueueToggle(id: number, leadQueueToggleAvailable: boolean): Observable<any> {
        return this._httpClient.put<any>(
            `${this.coreApiPath}/LeadAssignment/Recruiter/${id}/LeadQueueToggle/${leadQueueToggleAvailable}`,
            null
        );
    }

    addRecruiterToLineOfBusiness(recruiterLeadInfoId: number, lineOfBusinessId: number): Observable<any> {
        return this._httpClient.post<any>(
            `${this.coreApiPath}/LeadAssignment/RecruiterLeadInfo/${recruiterLeadInfoId}/LineOfBusiness/${lineOfBusinessId}`,
            null
        );
    }

    removeRecruiterFromLineOfBusiness(recruiterLeadInfoId: number, lineOfBusinessId: number): Observable<any> {
        return this._httpClient.delete<any>(
            `${this.coreApiPath}/LeadAssignment/RecruiterLeadInfo/${recruiterLeadInfoId}/LineOfBusiness/${lineOfBusinessId}`
        );
    }

    private getHeaders(date: Date = new Date(Date.now())): any {
        return { 'X-Timezone-Offset': date.getTimezoneOffset().toString() };
    }
}
